import { color } from "@rneui/base";
import React, { useCallback, useContext, useMemo, useRef } from "react";
import { StyleSheet, View } from "react-native";
import { Card, useTheme } from "react-native-elements";
import {
  allocateCreaturesToDungeonFloor,
  calculateCreatureCap,
  getFreeCreatures,
  isDungeonFloorValidForCreatures,
} from "../backend/exploration/Creatures";
import { Dungeon } from "../backend/exploration/dungeons/Dungeon";
import { DungeonFloor } from "../backend/exploration/dungeons/DungeonFloor";
import { getUnlockedDungeons } from "../backend/exploration/dungeons/Dungeons";
import { PlayerContext } from "../backend/PlayerContext";
import {
  getTemporaryEffectEndTime,
  hasTemporaryEffect,
} from "../backend/timetick/TemporaryEffects";
import { getSecondsPlayed } from "../backend/timetick/TotalTimePlayed";
import { formatNumber, formatTime } from "../utils/FormattingUtils";
import { CreatureMessageLog } from "./creatures/CreatureMessageLog";
import ActionsContainer from "./utility/ActionsContainer";
import { Card as UtilityCard } from "./utility/Card";
import CategoryContainer from "./utility/CategoryContainer";
import { CheckBox } from "./utility/CheckBox";
import { PlusMinusAllocator } from "./utility/PlusMinusAllocator";
import { Text } from "./utility/Text";
import { UIContext, useUIStateFromContext } from "./utility/UIContext";

function DungeonFloorRow(props: {
  floor: DungeonFloor;
  applyAll: boolean;
  isOdd: boolean;
}) {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;
  const freeCreatures = getFreeCreatures(playerContext);
  const hasFreeCreatures = freeCreatures > 0;
  const floor = props.floor;
  const current =
    playerContext.creatures?.creatureAllocation?.[floor.getId()] ?? 0;

  const onPressMinus = useCallback(
    () =>
      playerContextRef.current.apply(
        allocateCreaturesToDungeonFloor(
          floor.getId(),
          props.applyAll ? -Infinity : -1,
        ),
      ),
    [floor, props.applyAll],
  );
  const onPressPlus = useCallback(
    () =>
      playerContextRef.current.apply(
        allocateCreaturesToDungeonFloor(
          floor.getId(),
          props.applyAll ? +Infinity : 1,
        ),
      ),
    [floor, props.applyAll],
  );

  const floorNameText = useMemo(
    () => (
      <Text numberOfLines={2} style={styles.floorNameText}>
        {floor.getFloorName()} (Lv.{floor.getBreedingLevel()})
      </Text>
    ),
    [floor],
  );

  const plusMinusAllocator = useMemo(
    () => (
      <View style={styles.row}>
        <Text>{current}</Text>
        <View style={styles.plusMinusAllocatorContainer}>
          <PlusMinusAllocator
            disableMinus={current === 0}
            disablePlus={!hasFreeCreatures}
            onPressMinus={onPressMinus}
            onPressPlus={onPressPlus}
          />
        </View>
      </View>
    ),
    [current, hasFreeCreatures, onPressMinus, onPressPlus],
  );

  const theme = useTheme();

  return useMemo(
    () => (
      <View
        key={floor.getId()}
        style={[
          styles.row,
          styles.singleFloorContainer,
          props.isOdd && {
            backgroundColor: color(theme.theme.colors.grey5).alpha(0.5),
          },
        ]}
      >
        {floorNameText}
        {plusMinusAllocator}
      </View>
    ),
    [floorNameText, plusMinusAllocator, floor.getId(), props.isOdd, theme],
  );
}

function DungeonView(props: { dungeon: Dungeon; applyAll: boolean }) {
  const dungeon = props.dungeon;
  const playerContext = useContext(PlayerContext);
  const unlockedFloors = dungeon
    .getDungeonFloors()
    .filter((floor) =>
      isDungeonFloorValidForCreatures(playerContext, floor.getId()),
    );
  return useMemo(() => {
    if (unlockedFloors.length === 0) {
      return null;
    }
    return (
      <CategoryContainer key={dungeon.getId()} title={dungeon.getName()}>
        <View style={styles.dungeonContentsContainer}>
          {unlockedFloors.map((floor, index) => {
            return (
              <DungeonFloorRow
                key={floor.getId()}
                floor={floor}
                isOdd={index % 2 != 0}
                applyAll={props.applyAll}
              />
            );
          })}
        </View>
      </CategoryContainer>
    );
  }, [
    dungeon,
    unlockedFloors.map((floor) => floor.getId()).join(),
    props.applyAll,
  ]);
}

export function Creatures() {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;

  const uiContext = useContext(UIContext);

  const [applyAll, setApplyAll] = useUIStateFromContext(
    uiContext,
    "creatures_applyall",
    false,
  );

  const toggleApplyAll = useCallback(() => {
    setApplyAll(!applyAll);
  }, [applyAll, setApplyAll]);

  const dungeons = getUnlockedDungeons(playerContext);
  const dungeonsCard = useMemo(() => {
    return (
      <UtilityCard>
        <Card.Title>Dungeons</Card.Title>
        {dungeons.map((dungeon) => (
          <DungeonView
            key={dungeon.getId()}
            dungeon={dungeon}
            applyAll={applyAll}
          />
        ))}
      </UtilityCard>
    );
  }, [dungeons.map((dungeon) => dungeon.getId()).join(), applyAll]);

  const monsterCandyText = formatTime(
    getTemporaryEffectEndTime(playerContext, "Monster Candy") -
      getSecondsPlayed(playerContext),
    { shortForm: false, showDecimals: false },
  );
  const monsterCandyRow = useMemo(
    () =>
      hasTemporaryEffect(playerContext, "Monster Candy") ? (
        <View style={[styles.row, styles.creatureCapacityContainer]}>
          <Text>Monster Candy time left: </Text>
          <Text>{monsterCandyText}</Text>
        </View>
      ) : null,
    [monsterCandyText, hasTemporaryEffect(playerContext, "Monster Candy")],
  );

  const freeCreatures = getFreeCreatures(playerContext);
  const creatureCap = calculateCreatureCap(playerContext);
  const otherRows = useMemo(
    () => (
      <>
        <View style={styles.assignAllContainer}>
          <CheckBox
            title="Assign / unassign all at once"
            checked={applyAll}
            textStyle={styles.checkboxText}
            containerStyle={styles.checkboxContainer}
            onPress={toggleApplyAll}
          />
        </View>
        <View style={[styles.row, styles.totalCreaturesContainer]}>
          <Text>Creatures available: </Text>
          <Text>
            {formatNumber(freeCreatures)}/
            {formatNumber(playerContext.creatures.totalCreatures)}
          </Text>
        </View>
        <View style={[styles.row, styles.creatureCapacityContainer]}>
          <Text>Creature capacity: </Text>
          <Text>
            {formatNumber(playerContext.creatures.totalCreatures)}/
            {formatNumber(creatureCap)}
          </Text>
        </View>
      </>
    ),
    [
      applyAll,
      toggleApplyAll,
      freeCreatures,
      playerContext.creatures.totalCreatures,
      creatureCap,
    ],
  );

  return useMemo(() => {
    return (
      <View>
        {dungeonsCard}
        {otherRows}
        {monsterCandyRow}
        <View>
          <ActionsContainer area="Creatures" />
        </View>
        <View style={styles.messageContainer}>
          <CreatureMessageLog />
        </View>
      </View>
    );
  }, [dungeonsCard, otherRows, monsterCandyRow]);
}

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  plusMinusAllocatorContainer: {
    marginLeft: 16,
  },
  totalCreaturesContainer: {
    marginTop: 16,
    paddingHorizontal: 16,
  },
  creatureCapacityContainer: {
    marginTop: 4,
    paddingHorizontal: 16,
  },
  singleFloorContainer: {
    padding: 4,
  },
  messageContainer: {
    height: 300,
    padding: 8,
    borderColor: "#444",
    borderWidth: 1,
    marginTop: 8,
  },
  floorNameText: {
    flex: 1,
    marginRight: 16,
  },
  assignAllContainer: {
    marginTop: 8,
    paddingHorizontal: 16,
  },
  checkboxContainer: {
    marginLeft: 0,
    marginTop: 8,
    padding: 0,
  },
  dungeonContentsContainer: {
    marginTop: 4,
  },
  checkboxText: {
    fontWeight: "normal",
  },
});
